import { useEffect, useState } from "react";
import { ChevronRight, Folder, Loader2, HardDrive } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

export const FolderPicker = ({ open, onOpenChange, onPick }) => {
  const [trail, setTrail] = useState([{ id: "root", name: "OneDrive" }]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const current = trail[trail.length - 1];

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError("");
    api.get("/onedrive/folders", { params: { parent: current.id } })
      .then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [open, current.id]);

  const path = "/" + trail.slice(1).map((t) => t.name).join("/");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="onedrive-folder-picker">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Add a music folder</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-1 text-xs text-zinc-500" data-testid="folder-breadcrumb">
          {trail.map((t, i) => (
            <span key={t.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} />}
              <button onClick={() => setTrail(trail.slice(0, i + 1))} data-testid={`folder-crumb-${i}`}
                className={`rounded px-1.5 py-0.5 hover:bg-zinc-100 ${i === trail.length - 1 ? "font-semibold text-zinc-900" : ""}`}>
                {i === 0 ? <HardDrive size={12} className="mr-1 inline" /> : null}{t.name}
              </button>
            </span>
          ))}
        </div>
        <div className="h-72 overflow-y-auto rounded-md border border-zinc-200">
          {error && <p className="p-4 text-sm text-red-600" data-testid="folder-picker-error">{error}</p>}
          {!data && !error && <Loader2 className="m-4 animate-spin text-zinc-400" />}
          {data && data.folders.length === 0 && <p className="p-4 text-sm text-zinc-500">No subfolders here.</p>}
          {data?.folders.map((f) => (
            <button key={f.id} onClick={() => setTrail([...trail, { id: f.id, name: f.name }])} data-testid={`folder-item-${f.id}`}
              className="flex w-full items-center gap-3 border-b border-zinc-100 px-4 py-2.5 text-left text-sm transition-colors last:border-0 hover:bg-zinc-50">
              <Folder size={16} className="text-amber-600" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="font-mono text-[11px] text-zinc-400">{f.child_count}</span>
              <ChevronRight size={14} className="text-zinc-300" />
            </button>
          ))}
        </div>
        <DialogFooter className="items-center gap-3 sm:justify-between">
          <span className="truncate font-mono text-xs text-zinc-500">{path}</span>
          <button onClick={() => onPick({ id: data?.id || current.id, name: current.name, path })} disabled={!data} data-testid="folder-picker-use-button"
            className="rounded-full bg-zinc-900 px-5 py-2 text-sm text-white disabled:opacity-40">Use this folder</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
